// @spec AUD-STR-025
// @spec AUD-STR-043
'use client';

import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { toast, Toaster } from 'sonner';

import {
  CollectionDetailPanel,
  CreateCollectionModal,
  EvidenceCollectionList,
  useCollection,
  useCollections,
  useCreateCollection,
  useRemoveEvidence,
  useUpdateCollectionStatus,
} from '@/features/evidence-collections';
import type {
  CollectionResponse,
  CollectionsQueryParams,
  CreateCollectionRequest,
  UpdateStatusRequest,
} from '@/features/evidence-collections';
import { EvidenceSettingsCard, useEvidenceSettings } from '@/features/settings/audit';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { ApiClientError } from '@/types/api';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/evidence-collections')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.evidenceCollections'),
  }),
  component: EvidenceCollectionsPage,
});

/**
 * Evidence Collections settings page for managing audit evidence.
 */
function EvidenceCollectionsPage() {
  const { t } = useTranslation('settings');
  const { isAdmin, roleName } = useOrgRole();

  // Filters and pagination state
  const [filters, setFilters] = React.useState<CollectionsQueryParams>({
    page: 1,
    pageSize: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = React.useState<string | null>(null);
  const [removingEvidenceId, setRemovingEvidenceId] = React.useState<string | null>(null);

  // Fetch collections
  const { data, isLoading, isError, error } = useCollections(filters);

  // Fetch selected collection details
  const {
    data: selectedCollection,
    isLoading: isLoadingCollection,
    isError: isCollectionError,
    error: collectionError,
  } = useCollection(selectedCollectionId ?? '', {
    enabled: Boolean(selectedCollectionId),
  });

  // Mutations
  const createMutation = useCreateCollection();
  const updateStatusMutation = useUpdateCollectionStatus(selectedCollectionId ?? '');
  const removeEvidenceMutation = useRemoveEvidence(selectedCollectionId ?? '');

  // Handlers
  const handleFiltersChange = (newFilters: Partial<CollectionsQueryParams>) => {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleCollectionClick = (collection: CollectionResponse) => {
    setSelectedCollectionId(collection.id);
  };

  const handleCloseDetail = () => {
    setSelectedCollectionId(null);
  };

  const handleCreateSubmit = (request: CreateCollectionRequest) => {
    createMutation.mutate(request, {
      onSuccess: () => {
        toast.success(t('settings:toast.collectionCreatedSuccessfully'));
        setIsCreateModalOpen(false);
      },
      onError: (err) => {
        const message = err instanceof ApiClientError ? err.message : 'Failed to create collection';
        toast.error(message);
      },
    });
  };

  const handleStatusChange = (request: UpdateStatusRequest) => {
    updateStatusMutation.mutate(request, {
      onSuccess: () => {
        toast.success(t('settings:toast.statusUpdatedTo2', { request: request.status }));
      },
      onError: (err) => {
        const message = err instanceof ApiClientError ? err.message : 'Failed to update status';
        toast.error(message);
      },
    });
  };

  const handleRemoveEvidence = (evidenceId: string) => {
    setRemovingEvidenceId(evidenceId);
    removeEvidenceMutation.mutate(evidenceId, {
      onSuccess: () => {
        toast.success(t('settings:toast.evidenceRemovedFromCollection'));
        setRemovingEvidenceId(null);
      },
      onError: (err) => {
        const message = err instanceof ApiClientError ? err.message : 'Failed to remove evidence';
        toast.error(message);
        setRemovingEvidenceId(null);
      },
    });
  };

  const canCreate = isAdmin || roleName === 'Compliance Officer';
  const canEdit = isAdmin || roleName === 'Compliance Officer';

  // Fetch evidence settings
  const {
    data: evidenceSettings,
    isLoading: isLoadingSettings,
    error: settingsError,
    refetch: refetchSettings,
  } = useEvidenceSettings();

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-8">
        {/* Evidence Settings Card (Admin only) */}
        {isAdmin && (
          <EvidenceSettingsCard
            settings={evidenceSettings ?? null}
            isLoading={isLoadingSettings}
            error={settingsError ?? null}
            isAdmin={isAdmin}
            onRetry={() => {
              void refetchSettings();
            }}
          />
        )}

        <EvidenceCollectionList
          collections={data?.items ?? []}
          total={data?.total ?? 0}
          page={filters.page ?? 1}
          totalPages={data?.totalPages ?? 1}
          isLoading={isLoading}
          isError={isError}
          error={error}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onPageChange={handlePageChange}
          onCollectionClick={handleCollectionClick}
          onCreateClick={() => {
            setIsCreateModalOpen(true);
          }}
          canCreate={canCreate}
        />

        <CreateCollectionModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
          }}
          onSubmit={handleCreateSubmit}
          isLoading={createMutation.isPending}
        />

        <CollectionDetailPanel
          collection={selectedCollection}
          isLoading={isLoadingCollection}
          isError={isCollectionError}
          error={collectionError}
          isOpen={Boolean(selectedCollectionId)}
          onClose={handleCloseDetail}
          onStatusChange={handleStatusChange}
          onRemoveEvidence={handleRemoveEvidence}
          isUpdatingStatus={updateStatusMutation.isPending}
          isRemovingEvidence={removeEvidenceMutation.isPending}
          removingEvidenceId={removingEvidenceId}
          canEdit={canEdit}
        />

        <Toaster position="top-right" richColors />
      </div>
    </div>
  );
}
